import { Beaker, CreditCard, Gauge, KeyRound, Settings, Users } from 'lucide-react';

import { EVERY_ROLE_SURFACE } from './sidebar-definition.types';

import type { SidebarDefinition } from './sidebar-definition.types';

/**
 * The nav rows about the WORKSPACE rather than about the agent — the
 * org/settings block, which since 2026-08-28 carries the limits row too.
 *
 * Split out of `sidebar-definitions.ts` when `/experience` (Adaptive
 * Compositions D4) took that file over the ~300-line ceiling
 * (`.claude/rules/architecture.md`). The cut is the seam the list already had:
 * everything above it describes what the assistant DOES for a visitor, and
 * everything here describes the account it does it under. The parent spreads
 * this in place, so the array's ORDER — which is the render order — is unchanged.
 */
export const WORKSPACE_SIDEBAR_DEFINITIONS: readonly SidebarDefinition[] = [
  {
    to: '/settings/workspace',
    labelKey: 'sidebar.workspaceSettings',
    icon: Settings,
    group: 'ORG',
    // Every role: this page holds "leave this workspace", which every member
    // needs. Its conversation-settings cards are `settings:admin` gated on the
    // server and hide themselves; the row must not.
    surface: EVERY_ROLE_SURFACE,
  },
  // Team read on `members:view` — invisible to viewer + editor, visible to
  // reviewer/admin/owner. API keys read on `api-keys:view` — invisible to the
  // viewer, visible to editor and above. Billing read on `billing:view` —
  // invisible to viewer + editor, visible to reviewer/admin/owner.
  { to: '/team', labelKey: 'sidebar.team', icon: Users, group: 'ORG', surface: 'members' },
  {
    to: '/settings/api-keys',
    labelKey: 'sidebar.apiKeys',
    icon: KeyRound,
    group: 'ORG',
    surface: 'api-keys',
  },
  {
    to: '/settings/billing',
    labelKey: 'sidebar.billing',
    icon: CreditCard,
    group: 'ORG',
    surface: 'billing',
  },
  // Limits & alerts — one row where there were three (spend caps, usage alarms,
  // rate limits), homed in ORG beside Billing: what the workspace costs, and
  // the cap on what it may cost. It had a group of its OWN until 2026-08-28,
  // which by then was a heading over a single row repeating it — see
  // `SidebarGroup`.
  //
  // It reads on `settings:view` rather than `billing:view`, and that is a
  // WIDENING chosen deliberately. The three rows it replaces were gated
  // differently — spend caps and alarms on `billing:view` (reviewer and above),
  // rate limits on `settings:view` (editor and above) — so an editor could see
  // exactly one of them. Gating the consolidated page on `billing:view` would
  // take the traffic controls away from an editor who has them today, which is
  // a real loss of access dressed up as a consolidation; gating on
  // `settings:view` keeps everyone's existing reach and lets the SERVER decide
  // the rest. It does: the spend-cap and usage-alarm resolvers still require
  // `billing:view`, so an editor can never read what this row now lets them
  // navigate to. The page reads that same permission and omits both money tabs
  // AND their queries, so an editor opening it gets the Traffic tab alone —
  // not three permission banners on a page that is working correctly.
  {
    to: '/limits',
    labelKey: 'sidebar.limits',
    icon: Gauge,
    group: 'ORG',
    surface: 'settings',
  },

  // SYN-205 — Labs: per-workspace experimental feature toggles. Homed in ORG &
  // SETTINGS: its `/settings/experimental` route makes ORG the natural home.
  {
    to: '/settings/experimental',
    labelKey: 'sidebar.experimental',
    icon: Beaker,
    group: 'ORG',
    // Every role: the Labs READ deliberately carries no permission beyond the
    // global guard (`experimental-features.resolver.ts`); only the toggle is
    // `settings:admin`. Gating the row on `settings` would hide a page a viewer
    // can legitimately read.
    surface: EVERY_ROLE_SURFACE,
  },
];
